import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, of } from 'rxjs';
import { ConfigService } from './config.service';

interface SimulationPayload {
  name: string;
  description?: string;
  uuid?: string;
  filters?: any[];
  values?: any[];
}

interface AppliedFilterPayloadItem {
  field: string;
  operator: string;
  value: any;
  logicalOperator: '' | 'AND' | 'OR';
  beginScope: string;
  endScope: string;
}

@Injectable({
  providedIn: 'root'
})
export class PriceFileSimulationService {
  private dropdownResponseCache = new Map<string, any>();
  private readonly APPLIED_FILTER_TRACE_KEY = '[APPLIED_FILTER_TRACE]';
  private readonly RUN_SAVE_APPLIED_FILTER_DEBUG_KEY = '[RUN_SAVE_APPLIED_FILTER_DEBUG]';
  private lastRunAppliedFilters: AppliedFilterPayloadItem[] = [];

  constructor(private http: HttpClient, private configService: ConfigService) { }

  private get apiUrl(): string {
    return this.configService.getPlatformApiUrl();
  }

  private logRunSaveAppliedFilters(stage: string, data: any): void {
    try {
      console.log(this.RUN_SAVE_APPLIED_FILTER_DEBUG_KEY, stage, JSON.parse(JSON.stringify(data)));
    } catch {
      console.log(this.RUN_SAVE_APPLIED_FILTER_DEBUG_KEY, stage, data);
    }
  }

  getLastRunAppliedFilters(): AppliedFilterPayloadItem[] {
    return this.cloneAppliedFilters(this.lastRunAppliedFilters);
  }

  private cloneAppliedFilters(appliedFilters: any[]): AppliedFilterPayloadItem[] {
    if (!Array.isArray(appliedFilters)) {
      return [];
    }

    return appliedFilters.map((filter: any) => ({
      ...filter,
      value: Array.isArray(filter?.value) ? [...filter.value] : filter?.value
    }));
  }

  saveSimulationQuote({ name, description, uuid, filters, values }: SimulationPayload): Observable<any> {
    const requestData: Record<string, any> = {
      entity: 'simulation',
      name,
      description: description ?? '',
      filters: filters ?? [],
      values: values ?? []
    };

    if (uuid) {
      requestData['uuid'] = uuid;
    }

    const payload = {
      requestData,
      responseList: [],
      entity: 'simulation',
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [
        {
          functionName: 'SimulationInsert',
          version: '1.0'
        }
      ],
      communicationType: 'rest',
      version: '1.0'
    };

    return this.http.post(this.apiUrl, payload);
  }

  saveSummary(data: {
    uuid: string;
    source_entity?: string;
    totalCurrentSalesValue: string;
    totalDollarImpact: string;
    totalImpactResult: string;
    totalCurrentAnnualUnits: number;
    simulatedFutureSales?: string;
    simulatedImpactUSD?: string;
    simulatedImpactPercent?: string;
    appliedFilters?: any[];
    priceChanges?: any[];
    summary?: any;
  }): Observable<any> {
    const sourceEntity = String(data.source_entity || '').trim() || 'dn_pricing';
    const appliedFilters = Array.isArray(data.appliedFilters) && data.appliedFilters.length > 0
      ? this.cloneAppliedFilters(data.appliedFilters)
      : this.resolveAppliedFiltersForPayload(data.appliedFilters);
    this.logRunSaveAppliedFilters('PriceFileSimulationService.saveSummary.resolvedAppliedFilters', {
      incomingAppliedFilters: data.appliedFilters,
      resolvedAppliedFilters: appliedFilters,
      lastRunAppliedFilters: this.lastRunAppliedFilters
    });
    console.log(this.APPLIED_FILTER_TRACE_KEY, 'saveSummary.appliedFilters', {
      incomingAppliedFilters: data.appliedFilters,
      resolvedAppliedFilters: appliedFilters
    });

    const payload = {
      entity: 'simulation',
      source_entity: sourceEntity,
      requestData: {
        entity: 'simulation',
        source_entity: sourceEntity,
        uuid: data.uuid,
        totalCurrentSalesValue: data.totalCurrentSalesValue,
        totalDollarImpact: data.totalDollarImpact,
        totalImpactResult: data.totalImpactResult,
        totalCurrentAnnualUnits: data.totalCurrentAnnualUnits,
        simulatedFutureSales: data.simulatedFutureSales ?? '',
        simulatedImpactUSD: data.simulatedImpactUSD ?? '',
        simulatedImpactPercent: data.simulatedImpactPercent ?? '',
        appliedFilters,
        priceChanges: data.priceChanges ?? [],
        summary: data.summary ?? {}
      },
      responseList: [],
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [
        {
          functionName: 'SaveSummary',
          version: '1.0'
        }
      ]
    };

    this.logRunSaveAppliedFilters('PriceFileSimulationService.saveSummary.finalPayload', {
      requestDataAppliedFilters: payload.requestData.appliedFilters,
      payload
    });

    return this.http.post(this.apiUrl, payload);
  }

  deleteSimulationByUuid(uuid: string): Observable<any> {
    const resolvedUuid = String(uuid || '').trim();
    if (!resolvedUuid) {
      return of(null);
    }

    const payload = {
      requestData: {
        entity: 'simulation',
        uuid: resolvedUuid
      },
      responseList: [],
      entity: 'simulation',
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [
        {
          functionName: 'DeleteSimulationByUuid',
          version: '1.0'
        }
      ],
      communicationType: 'rest',
      version: '1.0'
    };

    return this.http.post(this.apiUrl, payload);
  }

  getEntityOptions(uuid?: string): Observable<string[]> {
    return this.fetchDropdown('PasSimulationComputeFilters', this.withUuid({ entity: 'entity' }, uuid), false, ['Entity']);
  }

  getFieldOptions(selectedEntity?: string, uuid?: string): Observable<string[]> {
    return this.fetchDropdown(
      'SimFieldDrop',
      this.withUuid({ entity: this.resolveFieldEntity(selectedEntity) }, uuid),
      false,
      this.getFieldDropdownKeys(selectedEntity)
    );
  }

  getOperatorOptions(uuid?: string): Observable<string[]> {
    return this.fetchDropdown('SimOperatorDrop', this.withUuid({ entity: 'operator' }, uuid), false, ['operator']);
  }

  getValueOptions(field: string, selectedEntity?: string, uuid?: string): Observable<string[]> {
    const resolvedEntity = this.resolveFieldEntity(selectedEntity);
    return this.fetchDropdown(
      'SimValueDrop',
      this.withUuid({
        entity: resolvedEntity,
        field,
        filterName: `${resolvedEntity}_values`,
        offset: 0,
        limit: 5000
      }, uuid),
      true
    );
  }

  getPriceChangeTypeOptions(uuid?: string): Observable<string[]> {
    return this.fetchDropdown('PasSimulationComputeFilters', this.withUuid({ entity: 'entity' }, uuid), false, ['PriceChangeType']);
  }

  getPriceChangeValueOptions(priceChangeType: string = 'value', uuid?: string): Observable<string[]> {
    return this.fetchDropdown(
      'PasSimulationComputeFilters',
      this.withUuid({ entity: 'entity', priceChangeType }, uuid)
    );
  }

  getBasedOnOptions(uuid?: string): Observable<string[]> {
    return this.fetchDropdown('PasSimulationComputeFilters', this.withUuid({ entity: 'entity' }, uuid), false, ['BasedOn']);
  }

  getCachedFilterDialogOptions(selectedEntity?: string, uuid?: string): { fields: string[]; operators: string[] } {
    const resolvedEntity = this.resolveFieldEntity(selectedEntity);
    const cacheKey = this.buildDropdownCacheKey('PasSimulationComputeFilters', uuid);
    const dropdown = this.dropdownResponseCache.get(cacheKey)
      || this.dropdownResponseCache.get(this.buildDropdownCacheKey('PasSimulationComputeFilters', undefined))
      || null;

    if (!dropdown) {
      return { fields: [], operators: [] };
    }

    const fieldKeys = this.getFieldDropdownKeys(resolvedEntity);
    const fields = this.extractValuesFromDropdown(dropdown, fieldKeys, true);
    const operators = this.extractValuesFromDropdown(dropdown, ['operator'], true);
    return { fields, operators };
  }

  private resolveFieldEntity(selectedEntity?: string): string {
    const rawValue = String(selectedEntity || '').trim();
    if (!rawValue) {
      return 'base_price';
    }

    const lowerRaw = rawValue.toLowerCase();
    const labelPart = lowerRaw.replace(/^\d+\s*[-:]\s*/, '').trim();

    if (labelPart.includes('base price')) {
      return 'base_price';
    }
    if (labelPart.includes('dn pricing') || labelPart.includes('dn price')) {
      return 'dn_pricing';
    }

    const normalized = labelPart
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    return normalized || 'base_price';
  }

  private withUuid(requestData: any, uuid?: string): any {
    const resolvedUuid = String(uuid || '').trim();
    if (!resolvedUuid) {
      return requestData;
    }
    return {
      ...requestData,
      uuid: resolvedUuid
    };
  }

  runSimulation(requestData: any): Observable<any> {
    // Use the entity selected by the user from the dropdown
    const entity = requestData.entity || 'dn_pricing';

    // Convert filters from internal format (attributeName/operator/attributeValues)
    // to TotalParts format (field/operator/value) when scoped appliedFilters are not provided.
    const rawFilters = requestData.filter?.where || requestData.filters || [];
    const userFilters = rawFilters.map((f: any) => {
      const field = f.attributeName || f.field;
      const operator = f.operator || '=';
      const forceArray = this.isListOperator(operator);

      return {
        field,
        operator,
        value: this.normalizeFilterValueForPayload(
          Array.isArray(f?.attributeValues) ? f.attributeValues : f?.value,
          f?.multiValueInput === true || forceArray,
          forceArray
        )
      };
    });

    // Format priceChangeValue as string with suffix (e.g. "50$" or "4%")
    const pctType = requestData.priceChangeType?.toLowerCase() || 'percentage';
    const rawValue = requestData.priceChangeValue ?? 0;
    const suffix = pctType === 'percentage' ? '%' : '$';
    const priceChangeValue = `${rawValue}${suffix}`;

    this.logRunSaveAppliedFilters('PriceFileSimulationService.runSimulation.beforeResolveAppliedFilters', {
      incomingRequestDataAppliedFilters: requestData?.appliedFilters,
      rawFilters,
      userFilters
    });

    const appliedFilters = this.resolveAppliedFiltersForPayload(requestData?.appliedFilters, userFilters);
    this.lastRunAppliedFilters = this.cloneAppliedFilters(appliedFilters);
    this.logRunSaveAppliedFilters('PriceFileSimulationService.runSimulation.resolvedAppliedFilters', {
      incomingRequestDataAppliedFilters: requestData?.appliedFilters,
      userFilters,
      resolvedAppliedFilters: appliedFilters,
      cachedLastRunAppliedFilters: this.lastRunAppliedFilters
    });
    console.log(this.APPLIED_FILTER_TRACE_KEY, 'runSimulation.appliedFilters', {
      incomingAppliedFilters: requestData?.appliedFilters,
      userFilters,
      resolvedAppliedFilters: appliedFilters
    });

    const runRequestData = this.withUuid({
      entity: entity,

      appliedFilters: appliedFilters,
      priceChanges: [
        {
          priceChangeType: pctType,
          priceChangeValue: priceChangeValue,
          basedOn: requestData.basedOn || 'current_price'
        }
      ]
    }, requestData?.uuid);

    const payload = {
      entity: entity,
      requestData: runRequestData,
      responseList: [],
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [
        {
          functionName: 'TotalPartsNEW',
          version: '1.0'
        }
      ]
    };

    console.log(this.APPLIED_FILTER_TRACE_KEY  , 'runSimulation.payload.requestData.appliedFilters hello1', payload?.requestData?.appliedFilters);
    this.logRunSaveAppliedFilters('PriceFileSimulationService.runSimulation.finalPayload', {
      requestDataAppliedFilters: payload?.requestData?.appliedFilters,
      payload
    });
    console.log('[TotalParts] User filters:', JSON.stringify(userFilters, null, 2));
    console.log('[TotalParts] FULL payload:', JSON.stringify(payload, null, 2));

    return this.http.post(this.apiUrl, payload);
  }

  private getFieldDropdownKeys(selectedEntity?: string): string[] {
    const resolvedEntity = this.resolveFieldEntity(selectedEntity);
    if (resolvedEntity === 'base_price') {
      return ['BasePrice_Field', 'Base_Price_Field', 'base_price_field'];
    }
    if (resolvedEntity === 'dn_pricing') {
      return ['DN_Price_field', 'DN_Pricing_Field', 'DNPrice_Field', 'dn_price_field', 'dn_pricing_field'];
    }
    return [`${resolvedEntity}_field`, `${resolvedEntity}Field`];
  }

  private fetchDropdown(functionName: string, requestData: any, preserveRawValues = false, preferredKeys: string[] = []): Observable<string[]> {
    const payload = {
      requestData,
      responseList: [],
      entity: typeof requestData?.entity === 'string' ? requestData.entity : 'sim_quote',
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [
        {
          functionName,
          version: '1.0'
        }
      ],
      communicationType: 'rest',
      version: '1.0'
    };

    return this.http.post<any>(this.apiUrl, payload).pipe(
      map((response) => {
        const dropdown = response?.responseList?.[0]?.dropdown?.[0];
        if (dropdown) {
          const cacheKey = this.buildDropdownCacheKey(functionName, requestData?.uuid);
          this.dropdownResponseCache.set(cacheKey, dropdown);
        }
        return this.extractDropdownValues(response, preserveRawValues, preferredKeys);
      })
    );
  }

  private buildDropdownCacheKey(functionName: string, uuid?: string): string {
    const resolvedUuid = String(uuid || '').trim().toUpperCase();
    return `${functionName}::${resolvedUuid || '__NO_UUID__'}`;
  }

  private extractValuesFromDropdown(dropdown: any, preferredKeys: string[] = [], preserveRawValues = false): string[] {
    const wrapper = {
      responseList: [{ dropdown: [dropdown] }]
    };
    return this.extractDropdownValues(wrapper, preserveRawValues, preferredKeys);
  }

  private extractDropdownValues(response: any, preserveRawValues = false, preferredKeys: string[] = []): string[] {
    const dropdown = response?.responseList?.[0]?.dropdown?.[0];
    if (!dropdown) {
      return [];
    }

    // Some payloads include metadata keys (for example "operation") before the
    // actual dropdown field key (for example "operator"), so avoid key-order mapping.
    const metadataKeys = new Set(['operation', 'status', 'message', 'error', 'code']);
    const normalizeKey = (value: any): string =>
      String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '');

    const candidateKeys = Object.keys(dropdown);
    const preferredNormalized = preferredKeys.map((key) => normalizeKey(key)).filter((key) => key !== '');

    const preferredMatchedKey = preferredNormalized.length > 0
      ? candidateKeys.find((key) => preferredNormalized.includes(normalizeKey(key)))
      : undefined;

    const dropdownKey = preferredMatchedKey
      || (Object.prototype.hasOwnProperty.call(dropdown, 'operator') ? 'operator' : undefined)
      || candidateKeys.find((key) =>
        !metadataKeys.has(key) &&
        (Array.isArray(dropdown[key]) || typeof dropdown[key] === 'string' || typeof dropdown[key] === 'number')
      )
      || candidateKeys[0];

    const values = dropdown[dropdownKey];

    const formatValue = (v: any): string => {
      const strVal = String(v).trim();
      if (preserveRawValues) {
        return strVal;
      }
      // If the format is like '33{Brazil}', show as '33 - Brazil' (keep code)
      const match = strVal.match(/^([^{]+)\{([^}]+)\}$/);
      return match ? `${match[1].trim()} - ${match[2].trim()}` : strVal;
    };

    if (Array.isArray(values)) {
      return values.map(formatValue);
    }

    if (typeof values === 'string' || typeof values === 'number') {
      return [formatValue(values)];
    }

    return [];
  }

  getSimulationList(): Observable<any[]> {
    const url = this.apiUrl;
    const payload = {
      requestData: {
        uuid: [null]
      },
      responseList: [],
      entity: 'simulation',
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [
        {
          functionName: 'GetSimulationTable',
          version: '1.0'
        }
      ],
      communicationType: 'rest',
      version: '1.0'
    };

    console.log('[getSimulationList] URL:', url, 'payload:', JSON.stringify(payload));

    return this.http.post<any>(url, payload).pipe(
      map(response => {
        console.log('[getSimulationList] RAW response:', JSON.stringify(response, null, 2));

        // Response may be an array directly or nested
        let sims: any[] = [];
        const pickFirstArray = (source: any): any[] => {
          if (!source || typeof source !== 'object') {
            return [];
          }
          const preferredKeys = ['simulation', 'sim_quote', 'bar', 'data'];
          for (const key of preferredKeys) {
            if (Array.isArray(source[key])) {
              return source[key];
            }
          }
          const dynamicArrayKey = Object.keys(source).find((key) => Array.isArray(source[key]));
          return dynamicArrayKey ? source[dynamicArrayKey] : [];
        };

        if (Array.isArray(response)) {
          sims = response;
        } else if (Array.isArray(response?.responseList)) {
          response.responseList.forEach((responseItem: any) => {
            if (!responseItem || typeof responseItem !== 'object') {
              return;
            }
            const countBeforeItem = sims.length;

            // New shape: responseList[].data[].bar[]
            if (Array.isArray(responseItem.data)) {
              responseItem.data.forEach((dataItem: any) => {
                if (Array.isArray(dataItem)) {
                  sims.push(...dataItem);
                  return;
                }
                sims.push(...pickFirstArray(dataItem));
              });
            }

            // Existing/legacy shapes: responseList[].simulation[] etc.
            if (sims.length === countBeforeItem) {
              sims.push(...pickFirstArray(responseItem));
            }
          });
        } else if (response?.data && Array.isArray(response.data)) {
          sims = response.data;
        }

        if (sims.length > 0) {
          console.log('[getSimulationList] Sample record:', JSON.stringify(sims[0], null, 2));
        }

        return sims.map((s: any) => {
          const data = s.data || s;
          const resolvedUuid = s.quote_uuid || data.quote_uuid || s.uuid || data.uuid || data.simulation_uuid || data.id || '';
          return {
            ...data,
            id: data.id,
            quote_uuid: data.quote_uuid || s.quote_uuid || resolvedUuid,
            uuid: resolvedUuid
          };
        });
      })
    );
  }
  getSimulationSummary(uuid?: string): Observable<any[]> {
    return of([]); // no API call
  }

  getSimulationDetails(uuid: string): Observable<any> {
    const resolvedUuid = String(uuid || '').trim();
    if (!resolvedUuid) {
      return of(null);
    }

    const payload = {
      requestData: {
        entity: 'simulation',
        uuid: resolvedUuid
      },
      responseList: [],
      entity: 'simulation',
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [
        {
          functionName: 'GetSimulationDetails',
          version: '1.0'
        }
      ],
      communicationType: 'rest',
      version: '1.0'
    };

    return this.http.post<any>(this.apiUrl, payload).pipe(
      map((response: any) => {
        const simulationRecord = response?.responseList?.[0]?.simulation?.[0];
        const detailsData = simulationRecord?.data;
        if (!simulationRecord || !detailsData) {
          return response;
        }

        const sourceEntity =
          simulationRecord?.source_entity ??
          response?.responseList?.[0]?.source_entity ??
          response?.source_entity ??
          detailsData?.source_entity;

        const normalizedSourceEntity = String(sourceEntity ?? '').trim();
        if (!normalizedSourceEntity) {
          return response;
        }

        if (String(detailsData?.source_entity ?? '').trim() === normalizedSourceEntity) {
          return response;
        }

        return {
          ...response,
          responseList: [
            {
              ...response?.responseList?.[0],
              simulation: [
                {
                  ...simulationRecord,
                  data: {
                    ...detailsData,
                    source_entity: normalizedSourceEntity
                  }
                }
              ]
            },
            ...(response?.responseList?.slice(1) || [])
          ]
        };
      })
    );
  }

  getDeepDiveDetails(uuid: string, appliedFilters?: any[]): Observable<any> {
    const resolvedUuid = String(uuid || '').trim();
    if (!resolvedUuid) {
      return of(null);
    }

    const normalizedAppliedFilters = this.resolveAppliedFiltersForPayload(appliedFilters);
    console.log(this.APPLIED_FILTER_TRACE_KEY, 'getDeepDiveDetails.appliedFilters   hello2', {
      incomingAppliedFilters: appliedFilters,
      normalizedAppliedFilters
    });

    const payload = {
      requestData: {
        entity: 'simulation',
        uuid: resolvedUuid,
        contextType: 'region',
        ...(normalizedAppliedFilters.length > 0 ? { appliedFilters: normalizedAppliedFilters } : {})
      },
      responseList: [],
      entity: 'simulation',
      batchId: 'test',
      workflowId: 'ABC-123',
      endpoints: [
        {
          functionName: 'GetDeepDiveDetails',
          version: '1.0'
        }
      ],
      communicationType: 'rest',
      version: '1.0'
    };

    console.log(this.APPLIED_FILTER_TRACE_KEY, 'getDeepDiveDetails.payload.requestData.appliedFilters', payload?.requestData?.appliedFilters);
    return this.http.post<any>(this.apiUrl, payload);
  }

  private normalizeAppliedFiltersWithScopes(appliedFilters: any): AppliedFilterPayloadItem[] {
    if (!Array.isArray(appliedFilters)) {
      return [];
    }

    const normalized = appliedFilters
      .map((filter: any): AppliedFilterPayloadItem | null => {
        const field = String(filter?.field || filter?.attributeName || '').trim();
        if (!field) {
          return null;
        }

        const operator = String(filter?.operator || '=').trim();
        const value = Array.isArray(filter?.value)
          ? filter.value
          : String(filter?.value ?? '').trim();
        const logicalOperatorRaw = String(filter?.logicalOperator || '').trim().toUpperCase();
        const logicalOperator = logicalOperatorRaw === 'AND' || logicalOperatorRaw === 'OR'
          ? logicalOperatorRaw
          : '';

        return {
          field,
          operator,
          value,
          logicalOperator,
          beginScope: String(filter?.beginScope || ''),
          endScope: String(filter?.endScope || '')
        };
      })
      .filter((item): item is AppliedFilterPayloadItem => item !== null);

    // Ensure last filter does not carry a join operator.
    if (normalized.length > 0) {
      normalized[normalized.length - 1].logicalOperator = '';
    }

    return normalized;
  }

  private normalizeFilterValues(value: any, allowMultiSplit = false): string[] {
    if (Array.isArray(value)) {
      return value
        .map((item: any) => String(item ?? '').trim())
        .filter((item: string) => item !== '');
    }

    const raw = String(value ?? '').trim();
    if (!raw) {
      return [];
    }

    if (allowMultiSplit && raw.includes(',')) {
      return raw
        .split(',')
        .map((item: string) => item.trim())
        .filter((item: string) => item !== '');
    }

    if (allowMultiSplit) {
      const parts = raw
        .split(/\s+/)
        .map((item: string) => item.trim())
        .filter((item: string) => item !== '');
      return parts.length > 1 ? parts : [raw];
    }

    return [raw];
  }

  private isListOperator(operator: any): boolean {
    const op = String(operator || '').trim().toLowerCase();
    return op === 'in' || op === 'not_in' || op === 'not in';
  }

  private normalizeFilterValueForPayload(value: any, allowMultiSplit = false, forceArray = false): string | string[] {
    const normalizedValues = this.normalizeFilterValues(value, allowMultiSplit);
    if (normalizedValues.length === 0) {
      return forceArray ? [] : '';
    }
    if (!forceArray && normalizedValues.length <= 1) {
      return normalizedValues[0] ?? '';
    }
    return normalizedValues;
  }

  private resolveAppliedFiltersForPayload(appliedFilters: any, fallbackFilters: any[] = []): AppliedFilterPayloadItem[] {
    const normalizedScopedFilters = this.normalizeAppliedFiltersWithScopes(appliedFilters);
    console.log(this.APPLIED_FILTER_TRACE_KEY, 'resolveAppliedFiltersForPayload', {
      incomingAppliedFilters: appliedFilters,
      fallbackFilters,
      normalizedScopedFilters
    });
    if (normalizedScopedFilters.length > 0) {
      return normalizedScopedFilters;
    }
    const normalizedFlatFilters = this.normalizeFlatFiltersToAppliedFilters(fallbackFilters);
    console.log(this.APPLIED_FILTER_TRACE_KEY, 'resolveAppliedFiltersForPayload.fallbackNormalizedFlatFilters', normalizedFlatFilters);
    return normalizedFlatFilters;
  }

  private normalizeFlatFiltersToAppliedFilters(filters: any[]): AppliedFilterPayloadItem[] {
    if (!Array.isArray(filters) || filters.length === 0) {
      return [];
    }

    return filters
      .map((filter: any, index: number): AppliedFilterPayloadItem | null => {
        const field = String(filter?.field || filter?.attributeName || '').trim();
        if (!field) {
          return null;
        }
        return {
          field,
          operator: String(filter?.operator || '=').trim(),
          value: filter?.value ?? '',
          logicalOperator: index < filters.length - 1 ? 'AND' : '',
          beginScope: '',
          endScope: ''
        };
      })
      .filter((item): item is AppliedFilterPayloadItem => item !== null);
  }
}
